import type { MouseEventHandler, ReactNode } from "react";
import { Link as RouterLink, useInRouterContext } from "react-router-dom";

import { typography } from "./Typography/styles";
import { cn } from "@dsaui/utils";

export const rowTextClass = typography.profileDetail.className;

export interface ActionButtonProps {
  children: ReactNode;
  disabled?: boolean;
  fullWidth?: boolean;
  href?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  rel?: string;
  target?: string;
  variant?: "primary" | "secondary";
}

export const ActionButton = ({
  children,
  disabled = false,
  fullWidth = true,
  href,
  onClick,
  rel,
  target,
  variant = "primary",
}: ActionButtonProps) => {
  const isInRouterContext = useInRouterContext();
  const widthClass = fullWidth ? "block w-full" : "inline-block";
  const isClickable = !disabled && Boolean(href);
  const classes = cn(
    "rounded-md px-4 py-2.5 text-center no-underline shadow-sm ring-0 outline-hidden focus-visible:ring-2 focus-visible:ring-dsa-red focus-visible:ring-offset-2",
    widthClass,
    isClickable
      ? variant === "secondary"
        ? "border border-gray-300 bg-white text-label-sm text-gray-900 hover:border-dsa-red hover:bg-red-50 hover:text-dsa-red"
        : cn("bg-dsa-red", typography.buttonPrimaryLabel.className)
      : "border border-gray-300 bg-white text-label-sm text-gray-700 opacity-50",
  );

  if (!isClickable || !href) return <div className={classes}>{children}</div>;

  const isInternal =
    isInRouterContext && href.startsWith("/") && !href.startsWith("//");

  if (isInternal) {
    return (
      <RouterLink
        className={classes}
        onClick={onClick}
        rel={rel}
        target={target}
        to={href}
      >
        {children}
      </RouterLink>
    );
  }

  return (
    <a className={classes} href={href} onClick={onClick} rel={rel} target={target}>
      {children}
    </a>
  );
};

export const InfoRow = ({
  children,
  icon,
}: React.PropsWithChildren<{ icon: ReactNode }>) => (
  <div className="flex items-start gap-3">
    <div className="flex h-5 shrink-0 items-center text-gray-400">{icon}</div>
    <div className={`min-w-0 ${rowTextClass}`}>{children}</div>
  </div>
);
